"use client";

import { useEffect, useState } from "react";
import { Search, Mail, Calendar, MessageSquare } from "lucide-react";

const steps = [
  {
    number: 1,
    title: "Research in Seconds",
    description: "Ask Sabine to research any prospect or company. She pulls from LinkedIn, news, financials, and more — and saves it all to your account.",
    icon: Search,
  },
  {
    number: 2,
    title: "Personalized Outreach",
    description: "Sabine generates cold emails, LinkedIn messages, and call scripts tailored to each prospect using your offers and buyer personas.",
    icon: Mail,
  },
  {
    number: 3,
    title: "Auto-Prep for Meetings",
    description: "Connect your calendar and Sabine automatically prepares research briefs for upcoming meetings. Walk in informed without lifting a finger.",
    icon: Calendar,
  },
  {
    number: 4,
    title: "Follow Up with Confidence",
    description: "After your call, ask Sabine for follow-up emails, discovery questions, or account strategies — all personalized to what you learned.",
    icon: MessageSquare,
  },
];

export default function HowItWorksSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    const section = document.getElementById('how-it-works-section');
    if (section) observer.observe(section);
    return () => { if (section) observer.unobserve(section); };
  }, []);

  return (
    <section id="how-it-works-section" className="bg-slate-50 dark:bg-slate-900 py-20 lg:py-28">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-medium mb-4">
            How It Works
          </span>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl">
            From Research to Ready in Minutes
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-slate-400">
            Sabine handles the prep work so you can focus on the conversation.
          </p>
        </div>

        {/* Steps */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className={`relative bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-lg transition-all duration-500 ease-out ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center text-orange-500">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white text-sm font-bold flex items-center justify-center">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
